import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, X, Zap } from 'lucide-react';
import type { Node } from 'reactflow';
import type { FlowLlmNodePersistedData, LlmOperation, McpStep } from './llmWorkflowTypes';
import { MCP_SOFT_LIMIT, isLlmsSidebarFlowNode, operationPromptHint, slugifyNodeName } from './llmWorkflowTypes';
import { WorkflowConfigPanelMaximizeButton } from './WorkflowConfigPanelFrame';
import {
  getMustacheCompletionContext,
  layoutPromptSuggestMenuAtCaret,
  suggestionMatchInner,
} from './mustacheTextareaSuggest';

function outputKeyFromMustacheTemplate(template: string, fallback: string): string {
  const m = template.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/);
  return m ? m[1] : fallback;
}

function isManualDatabaseStep(step: McpStep): boolean {
  return Boolean(
    step.kind === 'database' &&
      step.database &&
      (step.paletteType.startsWith('MCP_Manual') || step.manualInputTemplate != null)
  );
}

/** One field per line: bare name (e.g. `documents`) or a full `{{ … }}` path (last segment becomes the key). */
function lineToExtraOutputStorageKey(line: string): string | null {
  const t = line.trim();
  if (!t) return null;
  let inner = t;
  const m = t.match(/^\{\{\s*([^}]+?)\s*\}\}$/);
  if (m) inner = m[1].trim();
  const segments = inner.split('.').map((s) => s.trim()).filter(Boolean);
  const field = segments.length ? segments[segments.length - 1] : inner;
  const slug = slugifyNodeName(field).replace(/^_+|_+$/g, '');
  return slug || null;
}

function parseExtraOutputFieldsFromTextarea(text: string, mcpSteps: McpStep[]): string[] {
  const reserved = new Set<string>(['output', 'event_emitted', ...mcpSteps.map((s) => s.outputKey)]);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const key = lineToExtraOutputStorageKey(rawLine);
    if (!key || reserved.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/** Cursor is inside an incomplete `@token` (same line, not an email-style `word@`). */
function getAtMentionContext(text: string, cursor: number): { atStart: number; filter: string } | null {
  const before = text.slice(0, cursor);
  const at = before.lastIndexOf('@');
  if (at === -1) return null;
  if (at > 0 && /[A-Za-z0-9_]/.test(before[at - 1])) return null;
  const afterAt = before.slice(at + 1);
  if (afterAt.includes('\n')) return null;
  return { atStart: at, filter: afterAt };
}

type PromptSuggestRow = { insert: string; display: string };

function atMentionRowMatches(row: PromptSuggestRow, filter: string): boolean {
  const f = filter.trim().toLowerCase();
  if (!f) return true;
  const token = row.insert.startsWith('@') ? row.insert.slice(1).toLowerCase() : row.insert.toLowerCase();
  const disp = row.display.toLowerCase();
  return token.startsWith(f) || disp.includes(f);
}

type LLMConfigPanelProps = {
  node: Node<FlowLlmNodePersistedData>;
  panelMaximized?: boolean;
  onTogglePanelMaximize?: () => void;
  /** Output templates from parent node(s) on the graph; primary input references for this LLM. */
  parentConnectorOutputHints: string[];
  onUpdate: (nodeId: string, patch: Partial<FlowLlmNodePersistedData>) => void;
  onReorderMcp: (nodeId: string, fromIndex: number, toIndex: number) => void;
  onRemoveMcp: (nodeId: string, stepId: string) => void;
  onAddBlankMcp: (nodeId: string) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
};

const OPS: LlmOperation[] = ['Analyze', 'Summarize', 'Generate', 'Classify', 'Extract'];

const VAR_SUGGESTIONS = [
  '{{input}}',
  '{{trigger.payload}}',
  '{{trigger.variables}}',
  '{{trigger.timestamp}}',
  '{{llm.output}}',
];

export default function LLMConfigPanel({
  node,
  panelMaximized = false,
  onTogglePanelMaximize,
  parentConnectorOutputHints,
  onUpdate,
  onReorderMcp,
  onRemoveMcp,
  onAddBlankMcp,
  onDelete,
  onClose,
}: LLMConfigPanelProps) {
  const d = node.data;
  const isLlmsPalette = isLlmsSidebarFlowNode(d);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [dragMcpIndex, setDragMcpIndex] = useState<number | null>(null);

  useEffect(() => {
    if (selectedStepId && !d.mcpSteps.some((s) => s.id === selectedStepId)) {
      setSelectedStepId(null);
    }
  }, [d.mcpSteps, selectedStepId]);

  const extraKeysJoined = (d.extraOutputKeys ?? []).join('\n');
  /** Prefer saved verbatim textarea; fall back to joined keys for older flows. */
  const outputsTextFromNode = d.extraOutputsText != null ? d.extraOutputsText : extraKeysJoined;

  const [outputsTextDraft, setOutputsTextDraft] = useState(() => outputsTextFromNode);

  useEffect(() => {
    setOutputsTextDraft(outputsTextFromNode);
  }, [node.id, outputsTextFromNode]);

  const extraOutputMustacheLines = useMemo(
    () =>
      (d.extraOutputKeys ?? []).map((key) => {
        const slug = slugifyNodeName(key).replace(/^_+|_+$/g, '') || 'extra';
        return `{{ ${d.nodeName}.${slug} }}`;
      }),
    [d.extraOutputKeys, d.nodeName]
  );

  const outputsPreviewText = useMemo(() => {
    const fromDraft = parseExtraOutputFieldsFromTextarea(outputsTextDraft, d.mcpSteps);
    const lines = ['{{output}}', ...fromDraft.map((k) => `{{ ${d.nodeName}.${k} }}`)];
    return lines.join('\n');
  }, [d.nodeName, d.mcpSteps, outputsTextDraft]);

  const commitOutputsTextDraft = useCallback(() => {
    const nextKeys = parseExtraOutputFieldsFromTextarea(outputsTextDraft, d.mcpSteps);
    const prevKeys = d.extraOutputKeys ?? [];
    const keysSame = prevKeys.length === nextKeys.length && prevKeys.every((p, i) => p === nextKeys[i]);
    const prevText = d.extraOutputsText;
    const nextText = outputsTextDraft;
    const textSame = (prevText ?? '') === (nextText ?? '');
    if (!keysSame || !textSame) {
      onUpdate(node.id, { extraOutputKeys: nextKeys, extraOutputsText: nextText });
    }
  }, [d.extraOutputKeys, d.extraOutputsText, d.mcpSteps, node.id, onUpdate, outputsTextDraft]);

  const selectedStep = d.mcpSteps.find((s) => s.id === selectedStepId) ?? null;

  const allPromptSuggestions = useMemo(
    () =>
      Array.from(
        new Set([
          ...VAR_SUGGESTIONS,
          ...parentConnectorOutputHints,
          `{{ ${d.nodeName}.output }}`,
          ...extraOutputMustacheLines,
        ])
      ),
    [extraOutputMustacheLines, parentConnectorOutputHints, d.nodeName]
  );

  const mcpAtSuggestRows = useMemo<PromptSuggestRow[]>(() => {
    const seen = new Set<string>();
    return d.mcpSteps.map((s, i) => {
      let token =
        slugifyNodeName(s.label.replace(/\s+/g, '_')).replace(/^_+|_+$/g, '') || s.outputKey;
      if (seen.has(token)) {
        token = `${token}_${i + 1}`;
      }
      seen.add(token);
      return {
        insert: `@${token}`,
        display: `${i + 1}. ${s.label} — ${s.outputKey}`,
      };
    });
  }, [d.mcpSteps]);

  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
  const promptSuggestListRef = useRef<HTMLDivElement>(null);
  const replaceRangeRef = useRef<{ from: number; to: number } | null>(null);
  /** Keeps latest list + index for keyboard pick (avoids stale closure on Enter after Arrow). */
  const promptSuggestLiveRef = useRef<{ rows: PromptSuggestRow[]; index: number }>({ rows: [], index: 0 });
  const [promptSuggestOpen, setPromptSuggestOpen] = useState(false);
  const [promptSuggestRows, setPromptSuggestRows] = useState<PromptSuggestRow[]>([]);
  const [promptSuggestIndex, setPromptSuggestIndex] = useState(0);
  const [promptSuggestMenuGeom, setPromptSuggestMenuGeom] = useState<{
    top: number;
    left: number;
    maxHeight: number;
  } | null>(null);

  const updatePromptSuggestMenuGeom = useCallback(() => {
    const ta = promptTextareaRef.current;
    if (!ta || !promptSuggestOpen || !isLlmsPalette) {
      setPromptSuggestMenuGeom(null);
      return;
    }
    const cursor = ta.selectionStart ?? 0;
    setPromptSuggestMenuGeom(layoutPromptSuggestMenuAtCaret(ta, cursor));
  }, [isLlmsPalette, promptSuggestOpen]);

  const refreshPromptSuggest = useCallback(
    (text: string, cursor: number) => {
      const mustacheCtx = getMustacheCompletionContext(text, cursor);
      if (mustacheCtx) {
        const inserts = allPromptSuggestions.filter((s) => suggestionMatchInner(s, mustacheCtx.filter));
        if (inserts.length === 0) {
          replaceRangeRef.current = null;
          promptSuggestLiveRef.current = { rows: [], index: 0 };
          setPromptSuggestOpen(false);
          setPromptSuggestRows([]);
          setPromptSuggestMenuGeom(null);
          return;
        }
        const rows = inserts.map((insert) => ({ insert, display: insert }));
        replaceRangeRef.current = { from: mustacheCtx.braceStart, to: cursor };
        promptSuggestLiveRef.current = { rows, index: 0 };
        setPromptSuggestRows(rows);
        setPromptSuggestIndex(0);
        setPromptSuggestOpen(true);
        const taNow = promptTextareaRef.current;
        if (taNow) setPromptSuggestMenuGeom(layoutPromptSuggestMenuAtCaret(taNow, cursor, text));
        return;
      }

      const atCtx = getAtMentionContext(text, cursor);
      if (atCtx && mcpAtSuggestRows.length > 0) {
        const rows = mcpAtSuggestRows.filter((r) => atMentionRowMatches(r, atCtx.filter));
        if (rows.length === 0) {
          replaceRangeRef.current = null;
          promptSuggestLiveRef.current = { rows: [], index: 0 };
          setPromptSuggestOpen(false);
          setPromptSuggestRows([]);
          setPromptSuggestMenuGeom(null);
          return;
        }
        replaceRangeRef.current = { from: atCtx.atStart, to: cursor };
        promptSuggestLiveRef.current = { rows, index: 0 };
        setPromptSuggestRows(rows);
        setPromptSuggestIndex(0);
        setPromptSuggestOpen(true);
        const taNow = promptTextareaRef.current;
        if (taNow) setPromptSuggestMenuGeom(layoutPromptSuggestMenuAtCaret(taNow, cursor, text));
        return;
      }

      replaceRangeRef.current = null;
      promptSuggestLiveRef.current = { rows: [], index: 0 };
      setPromptSuggestOpen(false);
      setPromptSuggestRows([]);
      setPromptSuggestMenuGeom(null);
    },
    [allPromptSuggestions, mcpAtSuggestRows]
  );

  const applyPromptSuggestion = useCallback(
    (template: string) => {
      const ta = promptTextareaRef.current;
      const range = replaceRangeRef.current;
      if (!ta || !range) return;
      const value = ta.value;
      const replaceEnd = ta.selectionStart ?? value.length;
      const newText = value.slice(0, range.from) + template + value.slice(replaceEnd);
      onUpdate(node.id, { parentPrompt: newText });
      replaceRangeRef.current = null;
      promptSuggestLiveRef.current = { rows: [], index: 0 };
      setPromptSuggestOpen(false);
      setPromptSuggestRows([]);
      setPromptSuggestMenuGeom(null);
      const caret = range.from + template.length;
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(caret, caret);
      });
    },
    [node.id, onUpdate]
  );

  useLayoutEffect(() => {
    if (!promptSuggestOpen || !isLlmsPalette) return;
    const el = promptSuggestListRef.current?.querySelector(`[data-suggest-idx="${promptSuggestIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [promptSuggestIndex, promptSuggestOpen, isLlmsPalette]);

  useLayoutEffect(() => {
    updatePromptSuggestMenuGeom();
  }, [updatePromptSuggestMenuGeom, d.parentPrompt, promptSuggestRows, promptSuggestOpen]);

  useEffect(() => {
    if (!isLlmsPalette || !promptSuggestOpen) return;
    const ta = promptTextareaRef.current;
    const onReposition = () => updatePromptSuggestMenuGeom();
    window.addEventListener('resize', onReposition);
    ta?.addEventListener('scroll', onReposition, { passive: true });
    return () => {
      window.removeEventListener('resize', onReposition);
      ta?.removeEventListener('scroll', onReposition);
    };
  }, [isLlmsPalette, promptSuggestOpen, updatePromptSuggestMenuGeom]);

  useEffect(() => {
    if (!isLlmsPalette || !promptSuggestOpen) return;
    const onDocMouseDown = (ev: MouseEvent) => {
      const root = promptTextareaRef.current?.closest('.workflow-llm-panel__prompt-suggest-wrap');
      if (root && ev.target instanceof Node && root.contains(ev.target)) return;
      setPromptSuggestOpen(false);
      setPromptSuggestRows([]);
      setPromptSuggestMenuGeom(null);
      replaceRangeRef.current = null;
      promptSuggestLiveRef.current = { rows: [], index: 0 };
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isLlmsPalette, promptSuggestOpen]);

  useEffect(() => {
    promptSuggestLiveRef.current.index = promptSuggestIndex;
  }, [promptSuggestIndex]);

  useEffect(() => {
    promptSuggestLiveRef.current.rows = promptSuggestRows;
  }, [promptSuggestRows]);

  useEffect(() => {
    setPromptSuggestOpen(false);
    setPromptSuggestRows([]);
    setPromptSuggestMenuGeom(null);
    replaceRangeRef.current = null;
    promptSuggestLiveRef.current = { rows: [], index: 0 };
  }, [node.id]);

  const applySuggestedPrompt = () => {
    const hint = operationPromptHint(d.operation);
    onUpdate(node.id, { parentPrompt: hint });
  };

  const patchStep = (stepId: string, next: McpStep) => {
    onUpdate(node.id, {
      mcpSteps: d.mcpSteps.map((s) => (s.id === stepId ? next : s)),
    });
  };

  const onChipDragStart = (index: number) => (event: React.DragEvent) => {
    setDragMcpIndex(index);
    event.dataTransfer.setData('application/x-llm-panel-mcp-reorder', String(index));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onChipDropOnIndex = (toIndex: number) => (event: React.DragEvent) => {
    event.preventDefault();
    const fromStr = event.dataTransfer.getData('application/x-llm-panel-mcp-reorder');
    if (fromStr === '') return;
    const from = parseInt(fromStr, 10);
    if (Number.isNaN(from) || from === toIndex) return;
    onReorderMcp(node.id, from, toIndex);
    setDragMcpIndex(null);
  };

  const datalistId = `llm-vars-${node.id}`;

  return (
    <aside className="workflow-llm-panel" aria-label="LLM configuration">
      <div className="workflow-trigger-panel__head">
        <h2 className="workflow-llm-panel__title">LLM</h2>
        <div className="workflow-trigger-panel__head-actions">
          {onTogglePanelMaximize ? (
            <WorkflowConfigPanelMaximizeButton expanded={panelMaximized} onToggle={onTogglePanelMaximize} />
          ) : null}
          <button type="button" className="workflow-trigger-panel__close" onClick={onClose} aria-label="Close panel">
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      </div>
      <p className="workflow-llm-panel__subtitle">{d.label}</p>

      {d.mcpSteps.length >= MCP_SOFT_LIMIT && (
        <p className="workflow-llm-panel__warn">Soft limit: {MCP_SOFT_LIMIT} MCP steps — remove a step to add more.</p>
      )}

      <div className="workflow-trigger-panel__section">
        <label className="workflow-trigger-panel__label" htmlFor={`llm-nodename-${node.id}`}>
          Node name
        </label>
        <input
          id={`llm-nodename-${node.id}`}
          className="workflow-trigger-panel__input"
          value={d.nodeName}
          onChange={(e) => onUpdate(node.id, { nodeName: slugifyNodeName(e.target.value) })}
          placeholder="llm_orchestrator"
        />
      </div>

      <div className="workflow-trigger-panel__section">
        <span className="workflow-trigger-panel__label">Input</span>
        <p className="workflow-llm-panel__hint">
          Output parameters from the parent connector — reference them in Parent prompt below (same as graph wiring).
        </p>
        <pre className="workflow-trigger-panel__outputs workflow-llm-panel__parent-connector-inputs">
          {parentConnectorOutputHints.join('\n')}
        </pre>
      </div>

      {!isLlmsPalette ? (
        <div className="workflow-trigger-panel__section">
          <label className="workflow-trigger-panel__label" htmlFor={`llm-op-${node.id}`}>
            Operation
          </label>
          <select
            id={`llm-op-${node.id}`}
            className="workflow-trigger-panel__select"
            value={d.operation}
            onChange={(e) => onUpdate(node.id, { operation: e.target.value as LlmOperation })}
          >
            {OPS.map((op) => (
              <option key={op} value={op}>
                {op}
              </option>
            ))}
          </select>
          <button type="button" className="workflow-llm-panel__hint-btn" onClick={applySuggestedPrompt}>
            Insert suggested opening
          </button>
        </div>
      ) : null}

      <div className="workflow-trigger-panel__section">
        <label className="workflow-trigger-panel__label" htmlFor={`llm-prompt-${node.id}`}>
          Parent prompt
        </label>
        {isLlmsPalette ? (
          <div className="workflow-llm-panel__prompt-suggest-wrap">
            <textarea
              ref={promptTextareaRef}
              id={`llm-prompt-${node.id}`}
              className="workflow-llm-panel__textarea"
              rows={6}
              value={d.parentPrompt}
              onChange={(e) => {
                const v = e.target.value;
                onUpdate(node.id, { parentPrompt: v });
                const cursor = e.target.selectionStart ?? v.length;
                refreshPromptSuggest(v, cursor);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' || e.key === 'Delete') {
                  e.stopPropagation();
                }
                if (!promptSuggestOpen || promptSuggestRows.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setPromptSuggestIndex((i) => {
                    const next = Math.min(promptSuggestLiveRef.current.rows.length - 1, i + 1);
                    promptSuggestLiveRef.current.index = next;
                    return next;
                  });
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setPromptSuggestIndex((i) => {
                    const next = Math.max(0, i - 1);
                    promptSuggestLiveRef.current.index = next;
                    return next;
                  });
                } else if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const { rows, index } = promptSuggestLiveRef.current;
                  const pick = rows[index]?.insert;
                  if (pick) applyPromptSuggestion(pick);
                } else if (e.key === 'Tab') {
                  e.preventDefault();
                  const { rows, index } = promptSuggestLiveRef.current;
                  const pick = rows[index]?.insert;
                  if (pick) applyPromptSuggestion(pick);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setPromptSuggestOpen(false);
                  setPromptSuggestRows([]);
                  setPromptSuggestMenuGeom(null);
                  replaceRangeRef.current = null;
                  promptSuggestLiveRef.current = { rows: [], index: 0 };
                }
              }}
              onClick={(e) => refreshPromptSuggest(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)}
              onKeyUp={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') return;
                refreshPromptSuggest(e.currentTarget.value, e.currentTarget.selectionStart ?? 0);
              }}
              placeholder="You orchestrate…"
              autoComplete="off"
              spellCheck={false}
            />
            {promptSuggestOpen && promptSuggestRows.length > 0 && promptSuggestMenuGeom ? (
              <div
                ref={promptSuggestListRef}
                className="workflow-llm-panel__prompt-suggest workflow-llm-panel__prompt-suggest--at-caret"
                style={{
                  top: promptSuggestMenuGeom.top,
                  left: promptSuggestMenuGeom.left,
                  maxHeight: promptSuggestMenuGeom.maxHeight,
                }}
                role="listbox"
                aria-label="Variable and MCP step suggestions"
              >
                {promptSuggestRows.map((row, idx) => (
                  <button
                    key={`${row.insert}-${idx}`}
                    type="button"
                    role="option"
                    aria-selected={idx === promptSuggestIndex}
                    data-suggest-idx={idx}
                    className={`workflow-llm-panel__prompt-suggest-item${idx === promptSuggestIndex ? ' workflow-llm-panel__prompt-suggest-item--active' : ''}`}
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      applyPromptSuggestion(row.insert);
                    }}
                    onMouseEnter={() => {
                      setPromptSuggestIndex(idx);
                      promptSuggestLiveRef.current.index = idx;
                    }}
                    title={`${row.display} → ${row.insert}`}
                  >
                    {row.display}
                  </button>
                ))}
              </div>
            ) : null}
            <p className="workflow-llm-panel__hint">
              Type <code>{'{{'}</code> for variables, <code>@</code> for MCP sequence steps — arrows, Enter or Tab to
              insert, Esc to close.
            </p>
          </div>
        ) : (
          <>
            <textarea
              id={`llm-prompt-${node.id}`}
              className="workflow-llm-panel__textarea"
              rows={6}
              value={d.parentPrompt}
              onChange={(e) => onUpdate(node.id, { parentPrompt: e.target.value })}
              placeholder="You orchestrate…"
            />
            <datalist id={datalistId}>
              {allPromptSuggestions.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
            <p className="workflow-llm-panel__hint">Type <code>{'{{'}</code> for variable hints (browser datalist).</p>
          </>
        )}
      </div>

      <div className="workflow-trigger-panel__section">
        <span className="workflow-trigger-panel__label">MCP sequence</span>
        <ul className="workflow-llm-panel__mcp-list">
          {d.mcpSteps.map((step, index) => (
            <li
              key={step.id}
              className={`workflow-llm-panel__mcp-item${selectedStepId === step.id ? ' workflow-llm-panel__mcp-item--active' : ''}${
                step.kind === 'event' ? ' workflow-llm-panel__mcp-item--event' : ''
              }${dragMcpIndex === index ? ' workflow-llm-panel__mcp-item--drag' : ''}`}
              draggable
              onDragStart={onChipDragStart(index)}
              onDragEnd={() => setDragMcpIndex(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onChipDropOnIndex(index)}
            >
              <span className="workflow-llm-panel__mcp-grip">≡</span>
              <button type="button" className="workflow-llm-panel__mcp-main" onClick={() => setSelectedStepId(step.id)}>
                <span className="workflow-llm-panel__mcp-idx">{index + 1}.</span>
                <span className="workflow-llm-panel__mcp-label">{step.label}</span>
                <span className="workflow-llm-panel__mcp-key">{step.outputKey}</span>
              </button>
              <button
                type="button"
                className="workflow-llm-panel__mcp-x"
                aria-label="Remove step"
                onClick={() => onRemoveMcp(node.id, step.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
        {!isLlmsPalette && (
          <button
            type="button"
            className="workflow-llm-panel__add-mcp"
            disabled={d.mcpSteps.length >= MCP_SOFT_LIMIT}
            onClick={() => onAddBlankMcp(node.id)}
          >
            + Add MCP step
          </button>
        )}
      </div>

      {selectedStep && (
        <div className="workflow-trigger-panel__section workflow-llm-panel__step-editor">
          <div className="workflow-llm-panel__step-head">
            {selectedStep.kind === 'event' ? (
              <Zap size={16} className="workflow-llm-panel__step-zap" strokeWidth={2} aria-hidden />
            ) : null}
            <span className="workflow-trigger-panel__label" style={{ marginBottom: 0 }}>
              Step: {selectedStep.label}
            </span>
          </div>
          <StepFields step={selectedStep} onChange={(next) => patchStep(selectedStep.id, next)} />
        </div>
      )}

      <div className="workflow-llm-panel__advanced">
        <button type="button" className="workflow-llm-panel__advanced-toggle" onClick={() => setAdvancedOpen((o) => !o)}>
          {advancedOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          Advanced
        </button>
        {advancedOpen && (
          <div className="workflow-llm-panel__advanced-body">
            <label className="workflow-trigger-panel__label">Temperature</label>
            <input
              type="number"
              step={0.1}
              min={0}
              max={2}
              className="workflow-trigger-panel__input"
              value={d.temperature}
              onChange={(e) => onUpdate(node.id, { temperature: parseFloat(e.target.value) || 0 })}
            />
            <label className="workflow-trigger-panel__label">Max tokens</label>
            <input
              type="number"
              min={1}
              className="workflow-trigger-panel__input"
              value={d.maxTokens}
              onChange={(e) => onUpdate(node.id, { maxTokens: Math.max(1, parseInt(e.target.value, 10) || 2000) })}
            />
          </div>
        )}
      </div>

      <div className="workflow-trigger-panel__section">
        <span className="workflow-trigger-panel__label">Outputs</span>
        <p className="workflow-llm-panel__hint">
          Default field is <code>output</code> (<code>{`{{ ${d.nodeName}.output }}`}</code> in prompts). Add one extra
          output name per line (bare name or full <code>{`{{ … }}`}</code> — the last path segment is used). Values are
          saved when you leave this box.
        </p>
        <pre className="workflow-trigger-panel__outputs workflow-llm-panel__outputs">{outputsPreviewText}</pre>
        <label className="workflow-trigger-panel__label" htmlFor={`llm-extra-outputs-${node.id}`}>
          Output variables
        </label>
        <textarea
          id={`llm-extra-outputs-${node.id}`}
          className="workflow-llm-panel__textarea workflow-llm-panel__textarea--mono"
          rows={5}
          value={outputsTextDraft}
          onChange={(e) => setOutputsTextDraft(e.target.value)}
          onBlur={commitOutputsTextDraft}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' || e.key === 'Delete') {
              e.stopPropagation();
            }
          }}
          placeholder={`documents\n{{ ${d.nodeName}.some_field }}`}
          spellCheck={false}
          autoComplete="off"
        />
      </div>

      <div className="workflow-trigger-panel__actions">
        <button type="button" className="workflow-trigger-panel__btn workflow-trigger-panel__btn--danger" onClick={() => onDelete(node.id)}>
          Delete LLM node
        </button>
      </div>
    </aside>
  );
}

function StepFields({ step, onChange }: { step: McpStep; onChange: (s: McpStep) => void }) {
  if (step.kind === 'database' && step.database) {
    const db = step.database;
    if (isManualDatabaseStep(step)) {
      const inputVal = step.manualInputTemplate ?? db.query;
      const outputVal = step.manualOutputTemplate ?? `{{${step.outputKey}}}`;
      return (
        <>
          <p className="workflow-llm-panel__hint">Manual data model — adjust input/output templates.</p>
          <label className="workflow-trigger-panel__label">Input</label>
          <input
            className="workflow-trigger-panel__input"
            value={inputVal}
            onChange={(e) => {
              const v = e.target.value;
              onChange({
                ...step,
                manualInputTemplate: v,
                database: { ...db, query: v },
              });
            }}
            placeholder="{{default}}"
          />
          <label className="workflow-trigger-panel__label">Output</label>
          <input
            className="workflow-trigger-panel__input"
            value={outputVal}
            onChange={(e) => {
              const v = e.target.value;
              onChange({
                ...step,
                manualOutputTemplate: v,
                outputKey: outputKeyFromMustacheTemplate(v, step.outputKey),
              });
            }}
            placeholder="{{default}}"
          />
        </>
      );
    }
    return (
      <>
        <label className="workflow-trigger-panel__label">Table</label>
        <input
          className="workflow-trigger-panel__input"
          value={db.table}
          onChange={(e) => onChange({ ...step, database: { ...db, table: e.target.value } })}
        />
        <label className="workflow-trigger-panel__label">Query</label>
        <textarea
          className="workflow-llm-panel__textarea"
          rows={3}
          value={db.query}
          onChange={(e) => onChange({ ...step, database: { ...db, query: e.target.value } })}
        />
        <label className="workflow-trigger-panel__label">Operation</label>
        <select
          className="workflow-trigger-panel__select"
          value={db.operation}
          onChange={(e) =>
            onChange({
              ...step,
              database: { ...db, operation: e.target.value as 'read' | 'write' },
            })
          }
        >
          <option value="read">read</option>
          <option value="write">write</option>
        </select>
      </>
    );
  }

  if (step.kind === 'knowledge_base' && step.knowledgeBase) {
    const kb = step.knowledgeBase;
    return (
      <>
        <label className="workflow-trigger-panel__label">KB name</label>
        <input
          className="workflow-trigger-panel__input"
          value={kb.kbName}
          onChange={(e) => onChange({ ...step, knowledgeBase: { ...kb, kbName: e.target.value } })}
        />
        <label className="workflow-trigger-panel__label">Query template</label>
        <textarea
          className="workflow-llm-panel__textarea"
          rows={3}
          value={kb.queryTemplate}
          onChange={(e) => onChange({ ...step, knowledgeBase: { ...kb, queryTemplate: e.target.value } })}
        />
      </>
    );
  }

  if (step.kind === 'api' && step.api) {
    const api = step.api;
    return (
      <>
        <label className="workflow-trigger-panel__label">Endpoint reference</label>
        <input
          className="workflow-trigger-panel__input"
          value={api.endpointRef}
          onChange={(e) => onChange({ ...step, api: { ...api, endpointRef: e.target.value } })}
          placeholder="orders.lookup"
        />
      </>
    );
  }

  if (step.kind === 'event' && step.event) {
    const ev = step.event;
    return (
      <>
        <p className="workflow-llm-panel__event-banner">Event emission</p>
        <label className="workflow-trigger-panel__label">Event name</label>
        <input
          className="workflow-trigger-panel__input"
          value={ev.eventName}
          onChange={(e) => onChange({ ...step, event: { ...ev, eventName: e.target.value } })}
          placeholder="lead.enriched"
        />
        <label className="workflow-trigger-panel__label">Payload</label>
        <textarea
          className="workflow-llm-panel__textarea workflow-llm-panel__textarea--mono"
          rows={6}
          value={ev.payloadTemplate}
          onChange={(e) => onChange({ ...step, event: { ...ev, payloadTemplate: e.target.value } })}
        />
      </>
    );
  }

  return <p className="workflow-llm-panel__hint">Unknown step type.</p>;
}
